import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type {
    GetSubscriptionChannelResponse,
    IChannel,
    ImportChannelSnapshotPayload,
    ImportChannelSnapshotResponse,
} from "containers/Channels/types";
import { toastApiError } from "helpers/apiErrorToast";
import { downloadApiExport, getDateForFileName } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

export const fetchChannels = createAsyncThunk("channels/getChannels", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/channel");
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchChannel = createAsyncThunk("channels/getChannel", async (id: number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/channel/${id}`);
        return response.data.result[0];
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchDeleteChannel = createAsyncThunk(
    "channels/deleteChannels",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/channel/${id}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchCreateChannel = createAsyncThunk(
    "channels/createChannel",
    async (data: TChannelRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/channel", data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchUpdateChannel = createAsyncThunk(
    "channels/updateChannel",
    async ({ id, data }: { id: number; data: TChannelRequest }, { rejectWithValue }) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/channel/${id}`, data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchTitleChannels = createAsyncThunk(
    "channels/getTitleChannels",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${id}/channels`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchActivetedChannels = createAsyncThunk(
    "channels/fetchActivetedChannels",
    async (titleId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/title/${titleId}/termChannels`);
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const exportChannelList = createAsyncThunk("channels/exportChannelList", async () => {
    try {
        const response = await toast.promise(
            axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/channel/export`, {
                headers: { Accept: "*/*" },
            }),
            {
                pending: `${t("Export.Pending")}`,
                success: `${t("Export.Successful")}`,
                error: `${t("Export.Error")}`,
            },
        );
        const fileName = `export_channels_${getDateForFileName()}.xlsx`;
        downloadApiExport(response.data, fileName);
    } catch (error: any) {
        console.log(error);
    }
});

export const fetchChannelCompensationGroups = createAsyncThunk(
    "channels/getChannelCompensationGroups",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/channel/groups");
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const postChannelCompensationGroups = createAsyncThunk(
    "channels/postChannelCompensationGroups",
    async ({ name }: { name: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/channel/groups", { name });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get subscription channel data:
export const fetchSubscriptionChannel = createAsyncThunk(
    "channels/fetchSubscriptionChannel",
    async ({ channelId, periodId }: { channelId: number; periodId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get<GetSubscriptionChannelResponse>(
                import.meta.env.REACT_APP_API_ENDPOINT_V2 +
                    `/subscription/channel/${channelId}/period/${periodId}/aggregates`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Import/update subscription channel snapshot:
export const importChannelSnapshot = createAsyncThunk(
    "channels/importChannelSnapshot",
    async (payload: ImportChannelSnapshotPayload, { rejectWithValue }) => {
        try {
            const response = await axios.post<ImportChannelSnapshotResponse>(
                import.meta.env.REACT_APP_API_ENDPOINT_V2 + `/subscription/snapshots/channel`,
                payload,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

type TChannelRequest = {
    name: string;
    /** Omitted on create/update since the channel revamp — calculation bases live on the term. */
    type?: number | null;
    kind: number;
    hasMilestones: number;
    showNotes: boolean | number;
};

export interface ChannelCompensationGroup {
    id: number;
    name: string;
}

export interface CounterState {
    deleteChannelStatus: TFetchStatus;
    activedChannels: { channelId: number }[];
    activetedChannelsStatus: TFetchStatus;
    titleChannelsStatus: TFetchStatus;
    channelsStatus: TFetchStatus;
    channels: IChannel[];
    channel: IChannel | undefined;
    fetchChannelStatus: TFetchStatus;
    titleChannels: IChannel[];
    createChannelStatus: TFetchStatus;
    updateChannelStatus: TFetchStatus;
    channelCompensationGroups: ChannelCompensationGroup[];
    importChannelSnapshotStatus: TFetchStatus;
}

const initialState: CounterState = {
    deleteChannelStatus: "idle",
    activedChannels: [],
    activetedChannelsStatus: "idle",
    titleChannelsStatus: "idle",
    channelsStatus: "idle",
    channels: [],
    createChannelStatus: "idle",
    updateChannelStatus: "idle",
    channel: undefined,
    fetchChannelStatus: "idle",
    titleChannels: [],
    channelCompensationGroups: [],
    importChannelSnapshotStatus: "idle",
};

export const channelsSlice = createSlice({
    name: "channels",
    initialState,
    reducers: {
        setActivetedChannelsIdleStatus(state) {
            state.activetedChannelsStatus = "idle";
        },
        setDeleteChannelIdleStatus(state) {
            state.deleteChannelStatus = "idle";
        },
        setCreateChannelIdleStatus(state) {
            state.createChannelStatus = "idle";
        },
        setUpdateChannelIdleStatus(state) {
            state.updateChannelStatus = "idle";
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchChannels.fulfilled, (state, action) => {
            state.channelsStatus = "succeeded";
            state.channels = action.payload;
        });
        builder.addCase(fetchChannels.pending, (state) => {
            state.channelsStatus = "pending";
            state.channels = [];
        });
        builder.addCase(fetchChannels.rejected, (state, { payload }) => {
            state.channelsStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchTitleChannels.fulfilled, (state, action) => {
            state.titleChannels = action.payload;
            state.titleChannelsStatus = "succeeded";
        });
        builder.addCase(fetchTitleChannels.pending, (state) => {
            state.titleChannelsStatus = "pending";
        });
        builder.addCase(fetchTitleChannels.rejected, (state, { payload }) => {
            state.titleChannelsStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchActivetedChannels.pending, (state) => {
            state.activetedChannelsStatus = "pending";
        });
        builder.addCase(fetchActivetedChannels.fulfilled, (state, action) => {
            state.activedChannels = action.payload.map((item: any) => ({
                channelId: item.id,
            }));
            state.activetedChannelsStatus = "succeeded";
        });
        builder.addCase(fetchActivetedChannels.rejected, (state, { payload }) => {
            state.activetedChannelsStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchDeleteChannel.pending, (state) => {
            state.deleteChannelStatus = "pending";
        });
        builder.addCase(fetchDeleteChannel.fulfilled, (state, action) => {
            state.channels = [...state.channels].filter((channel) => channel.id !== action.meta.arg);
            toast.success(`${t("Channels.DeleteSucceeded")}`);
            state.deleteChannelStatus = "succeeded";
        });
        builder.addCase(fetchDeleteChannel.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.deleteChannelStatus = "failed";
        });

        builder.addCase(fetchChannel.pending, (state) => {
            state.fetchChannelStatus = "pending";
        });
        builder.addCase(fetchChannel.fulfilled, (state, action) => {
            state.fetchChannelStatus = "succeeded";
            state.channel = action.payload;
        });
        builder.addCase(fetchChannel.rejected, (state, { payload }) => {
            state.fetchChannelStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchCreateChannel.pending, (state) => {
            state.createChannelStatus = "pending";
        });
        builder.addCase(fetchCreateChannel.fulfilled, (state, action) => {
            toast.success(`${t("Channels.CreatedSucceeded")}`);
            state.createChannelStatus = "succeeded";
        });
        builder.addCase(fetchCreateChannel.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.createChannelStatus = "failed";
        });
        builder.addCase(fetchUpdateChannel.pending, (state) => {
            state.updateChannelStatus = "pending";
        });
        builder.addCase(fetchUpdateChannel.fulfilled, (state, action) => {
            toast.success(`${t("Channels.UpdatedSucceeded")}`);
            state.updateChannelStatus = "succeeded";
        });
        builder.addCase(fetchUpdateChannel.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.updateChannelStatus = "failed";
        });

        builder.addCase(fetchChannelCompensationGroups.fulfilled, (state, action) => {
            state.channelCompensationGroups = action.payload;
        });

        builder.addCase(postChannelCompensationGroups.fulfilled, (state, action) => {
            state.channelCompensationGroups.push(action.payload);
        });

        builder.addCase(importChannelSnapshot.pending, (state) => {
            state.importChannelSnapshotStatus = "pending";
        });
        builder.addCase(importChannelSnapshot.fulfilled, (state) => {
            state.importChannelSnapshotStatus = "succeeded";
        });
        builder.addCase(importChannelSnapshot.rejected, (state, { payload }) => {
            state.importChannelSnapshotStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
    },
});

// Action creators are generated for each case reducer function
export const {
    setActivetedChannelsIdleStatus,
    setDeleteChannelIdleStatus,
    setCreateChannelIdleStatus,
    setUpdateChannelIdleStatus,
} = channelsSlice.actions;

export default channelsSlice.reducer;
